import React from 'react';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import {
  IObjektVy,
  IDef,
  IBusinessCollectionProps,
} from '../../../../../../Types/ObjektVy';
import { findCollectionStatic } from '../../../../../../Model/avenyStaticModelUtils';
import {
  IFunctionMenuItem,
  SideBySideWidthType,
} from '../../../../../../Utils/ObjektVyUtils';
import ObjektVy from '../ObjektVy/ObjektVy';
import BusinessDataForm from '../BusinessDataForm/BusinessDataForm';
import { makeNodeQuery } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import { memo, useCallback, useContext } from 'react';
import {
  IFormData,
  removeIdsFromNewAssociations,
  IValidateFn,
} from '../BusinessDataForm/Utils/BusinessDataFormUtils';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import useUpdateMutation, {
  makeUpdateQuery,
} from '../../../../../../Components/QueryComponents/UpdateMutation/useUpdateMutation';
import {
  NewId,
  selectedId,
} from '../../../../../../Components/MasterDetail/MasterDetailUtils';
import { useSelectedDetailQuery } from '../../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';

interface IProps extends IBusinessCollectionProps {
  collectionName: string;
  vyer: IObjektVy[];
  funktionsMenyItems?: IFunctionMenuItem[];
  sidaVidSida?: boolean;
  sideBySideWidths?: SideBySideWidthType[];
  editImplemented?: boolean;
  validateFns?: IValidateFn[];
  validateHooks?: Array<() => IValidateFn>;
}

export const StandardObjektVy = memo((props: IProps) => {
  const businessmodel = findCollectionStatic(props.collectionName);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const dataDependencies = businessmodel
    ? props.vyer.flatMap(vy => vy.def(businessmodel).dataDependencies ?? [])
    : [];
  const query = makeNodeQuery(props.collectionName, dataDependencies);

  const updateMutation = makeUpdateQuery(
    props.collectionName,
    dataDependencies
  );

  const [mutation] = useUpdateMutation(
    updateMutation.unsafelyUnwrap(),
    selectedId(props.id)
  );
  const { nodeData } = useSelectedDetailQuery(query.unsafelyUnwrap(), props.id);
  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values);
      return mutation({
        variables: {
          db: activeDatabaseContext.activeDatabase || '',
          values: {
            id: props.id,
            _version: nodeData ? nodeData._version : 1,
            ...newValues,
          },
        },
      });
    },
    [mutation, activeDatabaseContext.activeDatabase, props.id, nodeData]
  );

  const validateFns: IValidateFn[] = [];
  props.validateHooks?.forEach(useHook => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const func = useHook();
    validateFns.push(func);
  });

  const comps = React.useMemo(() => {
    return businessmodel
      ? props.vyer.map((vy: IDef, idx): React.ReactElement<any> | number | string => {
          const Comp = vy.def(businessmodel);
          return (
            <Comp.comp
              id={props.id}
              config={{ parents: props.config?.parents ?? [] }}
              key={idx}
            />
          );
        })
      : businessmodel;
  }, [businessmodel, props.vyer, props.id, props.config?.parents]);

  if (!businessmodel) {
    return (
      <InternalError
        id="153618a0-88dd-425f-b3f3-329e89f131dc"
        message={`Businessmodel saknas för ${props.collectionName}`}
      />
    );
  }

  return (
    <BusinessDataForm
      isNew={props.id === NewId}
      model={businessmodel}
      fullScreen={props?.config?.fullPage ?? false}
      disableEditMode={!props.editImplemented}
      data={nodeData}
      updateMutation={updateFunction}
      validateFns={props.validateFns?.concat(validateFns) ?? validateFns}
    >
      <ObjektVy
        nodeLoading={false}
        sidaVidSida={props.sidaVidSida}
        funktionsMenyItems={props.funktionsMenyItems}
        sideBySideWidths={props.sideBySideWidths}
        editImplemented={props.editImplemented}
      >
        {comps}
      </ObjektVy>
    </BusinessDataForm>
  );
});

export default StandardObjektVy;
